import { JSON_LD_SCHEMA, JSON_LD_SCHEMA_URLS, KIJIJI_LOGO } from '../constants'
import { type ListingPrice } from '../../../../apps/nwa/src/generated/graphql-types'
import { type Listing } from '../../../../apps/nwa/src/types/search'
import {
  AUTOS_ATTRIBUTES,
  getAttributesDictionary,
  getTransmissionTypeLiteralKey,
} from '../../../../apps/nwa/src/components/srp/search-list/domain'
import { getBaseUrl } from '../../../../apps/nwa/src/utils/url'
import { type TFunction } from 'next-i18next'

export type AutosSeoAttributes = {
  brandName: string
  bodyType: string
  color: string
  driveWheelConfiguration: string | null
  fuelType: string
  itemCondition: string
  kilometers: string
  model: string
  numberOfDoors: string
  transmission: string
  vehicleConfiguration: string
  vehicleModelDate: string
  vehicleSeatingCapacity: string
  vehicleIdentificationNumber: string
  hasVin: boolean
}

const getDriveWheelConfiguration = (driveWheelConfiguration: string) => {
  switch (driveWheelConfiguration) {
    case 'awd':
      return JSON_LD_SCHEMA_URLS.AUTOS.ALL_WHEEL
    case '4x4':
      return JSON_LD_SCHEMA_URLS.AUTOS.FOUR_WHEEL
    case 'fwd':
      return JSON_LD_SCHEMA_URLS.AUTOS.FRONT_WHEEL
    case 'rwd':
      return JSON_LD_SCHEMA_URLS.AUTOS.REAR_WHEEL
    default:
      return null
  }
}

export const getAutosSeoAttributes = (
  attributes: Record<string, string[]>,
  t: TFunction
): AutosSeoAttributes => {
  const brandName = attributes[AUTOS_ATTRIBUTES.BRAND_NAME]?.[0]
  const bodyType = attributes[AUTOS_ATTRIBUTES.BODY_TYPE]?.[0]
  const color = attributes[AUTOS_ATTRIBUTES.COLOR]?.[0]

  const driveWheelConfiguration = getDriveWheelConfiguration(
    attributes[AUTOS_ATTRIBUTES.DRIVE_TRAIN]?.[0]
  )

  const fuelType = attributes[AUTOS_ATTRIBUTES.FUEL_TYPE]?.[0]

  const itemCondition =
    attributes[AUTOS_ATTRIBUTES.VEHICLE_TYPE]?.[0] === 'New'
      ? JSON_LD_SCHEMA_URLS.NEW_CONDITION
      : JSON_LD_SCHEMA_URLS.USED_CONDITION

  const kilometers = attributes[AUTOS_ATTRIBUTES.CAR_MILEAGE]?.[0]
  const model = attributes[AUTOS_ATTRIBUTES.MODEL]?.[0]
  const numberOfDoors = attributes[AUTOS_ATTRIBUTES.NUM_OF_DOORS]?.[0]

  const transmission = t(
    getTransmissionTypeLiteralKey(parseInt(attributes[AUTOS_ATTRIBUTES.TRANSMISSION]?.[0]))
  )

  const vehicleConfiguration = attributes[AUTOS_ATTRIBUTES.TRIM]?.[0]
  const vehicleModelDate = attributes[AUTOS_ATTRIBUTES.YEAR]?.[0]
  const vehicleSeatingCapacity = attributes[AUTOS_ATTRIBUTES.NUM_OF_SEATS]?.[0]
  const vehicleIdentificationNumber = attributes[AUTOS_ATTRIBUTES.VIN]?.[0]
  const hasVin = !!vehicleIdentificationNumber

  return {
    brandName,
    bodyType,
    color,
    driveWheelConfiguration,
    fuelType,
    itemCondition,
    kilometers,
    model,
    numberOfDoors,
    transmission,
    vehicleConfiguration,
    vehicleModelDate,
    vehicleSeatingCapacity,
    vehicleIdentificationNumber,
    hasVin,
  }
}

export const getAutosStructuredMarkup = (
  attributes: AutosSeoAttributes,
  title: string,
  description: string,
  mainImage: string | null | undefined,
  price: ListingPrice,
  url: string
) => {
  const {
    brandName,
    bodyType,
    color,
    driveWheelConfiguration,
    fuelType,
    itemCondition,
    kilometers,
    model,
    numberOfDoors,
    transmission,
    vehicleConfiguration,
    vehicleModelDate,
    vehicleSeatingCapacity,
    vehicleIdentificationNumber,
    hasVin,
  } = attributes

  return {
    // https://developers.google.com/search/docs/appearance/structured-data/vehicle-listing
    '@context': JSON_LD_SCHEMA_URLS.BASE,
    '@type': hasVin ? 'Car' : 'Vehicle',
    name: title,
    description,

    /* CAR REQUIRED PROPERTIES */
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    itemCondition,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: kilometers,
      unitCode: 'KMT',
    },
    model,
    offers: {
      '@type': 'Offer',
      // assuming that all listings are in stock
      availability: `${JSON_LD_SCHEMA}/InStock`,
      price: price.amount,
      priceCurrency: 'CAD',
    },
    // not all listings have a VIN
    vehicleIdentificationNumber: vehicleIdentificationNumber ?? null,
    vehicleModelDate,

    /* CAR RECOMMENDED PROPERTIES */
    bodyType,
    color,
    driveWheelConfiguration,
    image: mainImage || KIJIJI_LOGO,
    numberOfDoors,
    url: `${getBaseUrl()}${url}`,
    vehicleConfiguration, // vehicle trim
    vehicleEngine: {
      '@type': 'EngineSpecification',
      fuelType,
    },
    vehicleSeatingCapacity,
    vehicleTransmission: transmission,
  }
}

/**
 * returns structured markup for each listing on SRP
 * currently only supports Cars & Trucks
 * TODO: logic for determining category schema to use
 * @param listings topAds + organic listings
 */
export const getListingsStructuredMarkup = (listings: Listing[], t: TFunction) => {
  const itemListElement = listings.map((listing, index) => {
    const { title, description, imageUrls, price, seoUrl } = listing

    const attributesDictionary = getAttributesDictionary(listing.attributes)

    const attributes = getAutosSeoAttributes(attributesDictionary, t)

    const structuredMarkup = getAutosStructuredMarkup(
      attributes,
      title,
      description,
      imageUrls?.[0],
      price,
      seoUrl
    )

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: structuredMarkup,
    }
  })

  return itemListElement
}
